// ─── VirtualKalvi · Pre-Algebra (7th-grade math) ─────────────────────────
// Public student page: browse 10 topics → read lesson → take quiz at the end.
// Mirrors the bilingual + modal pattern from ReadingNotesPage.
// API:  GET /prealgebra/topics            → list
//       GET /prealgebra/topic?id|slug=…   → single topic + questions
// ────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BASE_API_URL } from "./config";

// ── Color tokens (math: deep teal + saffron accents) ─────────────────────
const C = {
  bg:        "#FBF8F2",       // warm cream page bg
  card:      "#FFFFFF",
  ink:       "#1F2937",       // near-black ink
  inkSoft:   "#475569",
  border:    "#E5DFD3",
  primary:   "#0D6E6E",       // deep teal (math feel)
  primarySoft:"#E6F2F1",
  accent:    "#E0892F",       // saffron
  accentSoft:"#FFEDD3",
  ok:        "#16A34A",
  okSoft:    "#DCFCE7",
  bad:       "#DC2626",
  badSoft:   "#FEE2E2",
};

const TA_FONT = `"Noto Sans Tamil","Latha","Nirmala UI","Tamil Sangam MN",sans-serif`;
const EN_FONT = `system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif`;

// ─── Tiny markdown-ish renderer (bold ** **, line breaks) ───────────────
function renderLessonText(raw, lang) {
  if (!raw) return null;
  // Split paragraphs on blank lines (and on actual \n in DB content)
  const text = raw.replace(/\\n/g, "\n");
  const blocks = text.split(/\n{2,}/);
  return blocks.map((blk, i) => {
    // Bold: **xyz** → <strong>xyz</strong>
    const lines = blk.split(/\n/);
    return (
      <p key={i} style={{
        margin: "0 0 14px 0",
        fontFamily: lang === "tamil" ? TA_FONT : EN_FONT,
        fontSize: 17, lineHeight: 1.7, color: C.ink,
      }}>
        {lines.map((ln, j) => {
          const parts = ln.split(/(\*\*[^*]+\*\*)/g);
          return (
            <React.Fragment key={j}>
              {parts.map((p, k) =>
                p.startsWith("**") && p.endsWith("**")
                  ? <strong key={k} style={{ color: C.primary }}>{p.slice(2, -2)}</strong>
                  : <React.Fragment key={k}>{p}</React.Fragment>
              )}
              {j < lines.length - 1 && <br />}
            </React.Fragment>
          );
        })}
      </p>
    );
  });
}

// ─── TopicCard (in the topic list view) ─────────────────────────────────
function TopicCard({ topic, lang, onOpen }) {
  const title   = lang === "tamil" ? topic.title_tamil   : topic.title_en;
  const summary = lang === "tamil" ? topic.summary_tamil : topic.summary_en;
  const titleFont = lang === "tamil" ? TA_FONT : EN_FONT;
  return (
    <button
      onClick={() => onOpen(topic)}
      style={{
        textAlign: "left", cursor: "pointer",
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
        padding: "18px 18px 16px", display: "flex", gap: 14, alignItems: "flex-start",
        transition: "transform .12s, box-shadow .12s",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 6px 18px rgba(13,110,110,0.15)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.04)";
      }}
    >
      <div style={{
        flex: "0 0 auto",
        width: 48, height: 48, borderRadius: 12,
        background: C.primarySoft, color: C.primary,
        display: "grid", placeItems: "center",
        fontWeight: 800, fontSize: 18, fontFamily: EN_FONT,
      }}>
        {String(topic.order_index).padStart(2, "0")}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: titleFont, fontWeight: 700, fontSize: 18,
          color: C.ink, lineHeight: 1.3, marginBottom: 6,
        }}>
          {title}
        </div>
        <div style={{
          fontFamily: titleFont, fontSize: 14, color: C.inkSoft, lineHeight: 1.5,
        }}>
          {summary}
        </div>
      </div>
    </button>
  );
}

// ─── QuizCard (one question, with feedback) ─────────────────────────────
function QuizCard({ q, lang, onAnswer }) {
  const [picked, setPicked] = useState(null);
  const text = lang === "tamil" ? q.question_text_tamil : q.question_text_en;
  const opts = lang === "tamil"
    ? [q.option_a_tamil, q.option_b_tamil, q.option_c_tamil, q.option_d_tamil]
    : [q.option_a_en,    q.option_b_en,    q.option_c_en,    q.option_d_en];
  const explanation = lang === "tamil" ? q.explanation_tamil : q.explanation_en;
  const correctIdx = "ABCD".indexOf((q.correct_option || "A").toUpperCase());
  const font = lang === "tamil" ? TA_FONT : EN_FONT;

  const click = (idx) => {
    if (picked !== null) return;
    setPicked(idx);
    onAnswer && onAnswer(q.id, idx === correctIdx);
  };

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
      padding: 18, marginBottom: 14,
    }}>
      <div style={{
        fontFamily: font, fontSize: 17, color: C.ink, fontWeight: 600,
        marginBottom: 14, lineHeight: 1.5,
      }}>
        {text}
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {opts.map((opt, idx) => {
          const letter = "ABCD"[idx];
          let bg = C.card, bd = C.border, fg = C.ink, chipBg = "#F1F5F9", chipFg = C.inkSoft;
          if (picked !== null) {
            if (idx === correctIdx) { bg = C.okSoft; bd = C.ok; chipBg = C.ok; chipFg = "#fff"; }
            else if (idx === picked) { bg = C.badSoft; bd = C.bad; chipBg = C.bad; chipFg = "#fff"; }
          }
          return (
            <button
              key={idx}
              onClick={() => click(idx)}
              disabled={picked !== null}
              style={{
                cursor: picked === null ? "pointer" : "default",
                background: bg, border: `1.5px solid ${bd}`, borderRadius: 10,
                padding: "10px 12px", display: "flex", gap: 10, alignItems: "center",
                textAlign: "left", fontFamily: font, fontSize: 15, color: fg,
                lineHeight: 1.4,
              }}
            >
              <span style={{
                flex: "0 0 auto",
                width: 26, height: 26, borderRadius: "50%",
                background: chipBg, color: chipFg,
                display: "grid", placeItems: "center",
                fontWeight: 800, fontFamily: EN_FONT, fontSize: 13,
              }}>{letter}</span>
              <span>{opt}</span>
            </button>
          );
        })}
      </div>
      {picked !== null && (
        <div style={{
          marginTop: 12, padding: "10px 12px",
          background: picked === correctIdx ? C.okSoft : C.badSoft,
          border: `1px solid ${picked === correctIdx ? C.ok : C.bad}`,
          borderRadius: 10, fontFamily: font, fontSize: 14,
          color: C.ink, lineHeight: 1.5,
        }}>
          <strong style={{ color: picked === correctIdx ? C.ok : C.bad }}>
            {picked === correctIdx
              ? (lang === "tamil" ? "சரி! ✓ " : "Correct! ✓ ")
              : (lang === "tamil" ? "தவறு. சரியான பதில் "  : "Not quite. Correct answer: ")}
          </strong>
          {picked !== correctIdx && (
            <strong>{"ABCD"[correctIdx]}. </strong>
          )}
          {explanation}
        </div>
      )}
    </div>
  );
}

// ─── Topic Detail View (lesson + quiz button → quiz modal) ──────────────
function TopicDetail({ topic, lang, onBack, onLangToggle }) {
  const titleFont = lang === "tamil" ? TA_FONT : EN_FONT;
  const title   = lang === "tamil" ? topic.title_tamil   : topic.title_en;
  const summary = lang === "tamil" ? topic.summary_tamil : topic.summary_en;
  const lesson  = lang === "tamil" ? topic.lesson_tamil  : topic.lesson_en;
  const formula = lang === "tamil" ? topic.formula_tamil : topic.formula_en;

  const [quizOpen, setQuizOpen] = useState(false);
  const [scores, setScores] = useState({}); // {qId: bool}

  const handleAnswer = useCallback((qId, correct) => {
    setScores((s) => ({ ...s, [qId]: correct }));
  }, []);

  const totalQs = (topic.questions || []).length;
  const attempted = Object.keys(scores).length;
  const correct   = Object.values(scores).filter(Boolean).length;
  const allDone   = totalQs > 0 && attempted === totalQs;
  const allRight  = allDone && correct === totalQs;

  // Reset quiz state when topic changes
  useEffect(() => {
    setQuizOpen(false);
    setScores({});
  }, [topic.id]);

  // ESC closes quiz modal
  useEffect(() => {
    if (!quizOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setQuizOpen(false); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [quizOpen]);

  let quizBtnLabel, quizBtnBg;
  if (totalQs === 0) {
    quizBtnLabel = lang === "tamil" ? "விரைவில் வினா" : "Quiz coming soon";
    quizBtnBg = C.inkSoft;
  } else if (allRight) {
    quizBtnLabel = lang === "tamil"
      ? `✅ அனைத்தும் சரி (${correct}/${totalQs}) — மீண்டும் பார்க்கவும்`
      : `✅ All correct (${correct}/${totalQs}) — view again`;
    quizBtnBg = C.ok;
  } else if (allDone) {
    quizBtnLabel = lang === "tamil"
      ? `🔄 மீண்டும் முயற்சிக்கவும் (${correct}/${totalQs})`
      : `🔄 Try again (${correct}/${totalQs})`;
    quizBtnBg = C.accent;
  } else if (attempted > 0) {
    quizBtnLabel = lang === "tamil"
      ? `▶ வினா தொடரவும் (${attempted}/${totalQs})`
      : `▶ Resume quiz (${attempted}/${totalQs})`;
    quizBtnBg = C.primary;
  } else {
    quizBtnLabel = lang === "tamil"
      ? `📝 வினா எடுக்கவும் (${totalQs} கேள்விகள்)`
      : `📝 Take the Quiz (${totalQs} questions)`;
    quizBtnBg = C.primary;
  }

  return (
    <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 16px 60px" }}>
      {/* Top bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8, padding: "14px 0",
        position: "sticky", top: 0, background: C.bg, zIndex: 10,
        borderBottom: `1px solid ${C.border}`,
      }}>
        <button onClick={onBack} style={{
          cursor: "pointer", background: "transparent", border: `1px solid ${C.border}`,
          borderRadius: 8, padding: "6px 12px", fontFamily: EN_FONT, fontSize: 14,
          color: C.inkSoft,
        }}>← {lang === "tamil" ? "தலைப்புகள்" : "Topics"}</button>
        <div style={{ flex: 1 }} />
        <button onClick={onLangToggle} style={{
          cursor: "pointer", background: C.primarySoft, color: C.primary,
          border: `1px solid ${C.primary}`, borderRadius: 8, padding: "6px 12px",
          fontFamily: EN_FONT, fontSize: 13, fontWeight: 700,
        }}>
          {lang === "tamil" ? "EN" : "தமிழ்"}
        </button>
      </div>

      {/* Title block */}
      <div style={{ paddingTop: 22 }}>
        <div style={{
          display: "inline-block", background: C.accentSoft, color: C.accent,
          fontFamily: EN_FONT, fontWeight: 700, fontSize: 12, letterSpacing: 0.5,
          padding: "4px 10px", borderRadius: 20, marginBottom: 10,
        }}>
          PRE-ALGEBRA · {String(topic.order_index).padStart(2, "0")}
        </div>
        <h1 style={{
          fontFamily: titleFont, fontSize: 30, lineHeight: 1.25,
          color: C.ink, margin: "0 0 10px 0",
        }}>{title}</h1>
        <div style={{
          fontFamily: titleFont, fontSize: 16, color: C.inkSoft, lineHeight: 1.6,
        }}>{summary}</div>
      </div>

      {/* Formula box */}
      {formula && formula.trim().length > 0 && (
        <div style={{
          marginTop: 22, padding: "14px 16px",
          background: C.primarySoft, border: `1px solid ${C.primary}33`,
          borderRadius: 12, display: "flex", gap: 12, alignItems: "center",
        }}>
          <div style={{
            flex: "0 0 auto", width: 36, height: 36, borderRadius: "50%",
            background: C.primary, color: "#fff",
            display: "grid", placeItems: "center", fontWeight: 800, fontFamily: EN_FONT,
          }}>fx</div>
          <div style={{
            fontFamily: lang === "tamil" ? TA_FONT : "Consolas, Menlo, monospace",
            fontSize: 16, color: C.ink, lineHeight: 1.5,
          }}>
            {formula}
          </div>
        </div>
      )}

      {/* Lesson body */}
      <div style={{ marginTop: 22 }}>
        {renderLessonText(lesson, lang)}
      </div>

      {/* Quiz CTA */}
      <div style={{ marginTop: 28, textAlign: "center" }}>
        <button
          onClick={() => totalQs > 0 && setQuizOpen(true)}
          disabled={totalQs === 0}
          style={{
            cursor: totalQs > 0 ? "pointer" : "not-allowed",
            background: quizBtnBg, color: "#fff", border: "none",
            borderRadius: 999, padding: "14px 26px",
            fontFamily: EN_FONT, fontWeight: 700, fontSize: 16,
            boxShadow: "0 4px 12px rgba(13,110,110,0.25)",
          }}
        >
          {quizBtnLabel}
        </button>
        <div style={{
          marginTop: 10, fontFamily: lang === "tamil" ? TA_FONT : EN_FONT,
          fontSize: 13, color: C.inkSoft,
        }}>
          {lang === "tamil"
            ? "முதலில் வாசிக்கவும் — பின்னர் வினா எடுக்கவும்."
            : "Read the lesson first — then test your understanding."}
        </div>
      </div>

      {/* Quiz Modal */}
      {quizOpen && (
        <div
          onClick={() => setQuizOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
            backdropFilter: "blur(4px)", zIndex: 3000,
            display: "flex", alignItems: "flex-start", justifyContent: "center",
            padding: "40px 16px", overflow: "auto",
            animation: "fadeIn .2s ease-out",
          }}
        >
          <style>{`@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              background: C.bg, borderRadius: 16, maxWidth: 640, width: "100%",
              padding: "22px 22px 28px", boxShadow: "0 18px 48px rgba(0,0,0,0.35)",
            }}
          >
            <button
              onClick={() => setQuizOpen(false)}
              aria-label="Close"
              style={{
                position: "absolute", top: 12, right: 12,
                width: 32, height: 32, borderRadius: "50%",
                background: "#fff", border: `1px solid ${C.border}`,
                cursor: "pointer", color: C.inkSoft, fontFamily: EN_FONT,
                fontSize: 18, fontWeight: 700,
              }}
            >×</button>

            <div style={{
              display: "inline-block", background: C.primary, color: "#fff",
              fontFamily: EN_FONT, fontWeight: 700, fontSize: 12, letterSpacing: 0.5,
              padding: "4px 10px", borderRadius: 20, marginBottom: 8,
            }}>
              📝 QUIZ TIME
            </div>
            <div style={{
              fontFamily: titleFont, fontWeight: 700, fontSize: 18,
              color: C.ink, marginBottom: 4,
            }}>{title}</div>
            <div style={{
              fontFamily: EN_FONT, fontSize: 13, color: C.inkSoft, marginBottom: 16,
            }}>
              {lang === "tamil"
                ? `${totalQs} கேள்விகள் · சரியானவை ${correct}`
                : `${totalQs} questions · ${correct} correct so far`}
            </div>

            {(topic.questions || []).map((q) => (
              <QuizCard key={q.id} q={q} lang={lang} onAnswer={handleAnswer} />
            ))}

            {allDone && (
              <div style={{
                marginTop: 14, padding: "14px 16px",
                background: allRight ? C.okSoft : C.accentSoft,
                border: `1px solid ${allRight ? C.ok : C.accent}`,
                borderRadius: 12, textAlign: "center",
              }}>
                <div style={{ fontFamily: EN_FONT, fontWeight: 800, fontSize: 22, color: allRight ? C.ok : C.accent }}>
                  {correct} / {totalQs}
                </div>
                <div style={{ fontFamily: titleFont, fontSize: 14, color: C.ink, marginTop: 4 }}>
                  {allRight
                    ? (lang === "tamil" ? "சிறப்பாக செய்தீர்கள்! 🎉" : "Excellent work! 🎉")
                    : (lang === "tamil" ? "மீண்டும் பாடம் வாசிக்கவும்." : "Re-read the lesson and try again.")}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Topic List View ─────────────────────────────────────────────────────
function TopicList({ topics, lang, onOpen, onLangToggle }) {
  return (
    <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 16px 60px" }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "20px 0 14px", borderBottom: `1px solid ${C.border}`,
        marginBottom: 22,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: C.primary, color: "#fff",
          display: "grid", placeItems: "center", fontWeight: 800, fontFamily: EN_FONT,
        }}>π</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: EN_FONT, fontSize: 12, color: C.accent, fontWeight: 700, letterSpacing: 0.4 }}>
            VIRTUALKALVI · CLASS 7
          </div>
          <div style={{ fontFamily: EN_FONT, fontSize: 22, color: C.ink, fontWeight: 800 }}>
            {lang === "tamil" ? "முன் இயற்கணிதம்" : "Pre-Algebra"}
          </div>
        </div>
        <button onClick={onLangToggle} style={{
          cursor: "pointer", background: C.primarySoft, color: C.primary,
          border: `1px solid ${C.primary}`, borderRadius: 8, padding: "6px 12px",
          fontFamily: EN_FONT, fontSize: 13, fontWeight: 700,
        }}>
          {lang === "tamil" ? "EN" : "தமிழ்"}
        </button>
      </div>

      <div style={{
        fontFamily: lang === "tamil" ? TA_FONT : EN_FONT, fontSize: 15,
        color: C.inkSoft, marginBottom: 18, lineHeight: 1.6,
      }}>
        {lang === "tamil"
          ? "10 பாடங்கள். ஒவ்வொன்றிலும் எளிய விளக்கம் + வினா. முதல் தலைப்பிலிருந்து தொடங்கவும்."
          : "10 topics. Each has a short lesson + a quiz. Start at the top and work through."}
      </div>

      <div style={{ display: "grid", gap: 12 }}>
        {topics.map((t) => (
          <TopicCard key={t.id} topic={t} lang={lang} onOpen={onOpen} />
        ))}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────
export default function PreAlgebraPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [lang, setLang] = useState("english"); // 'english' | 'tamil'
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTopic, setActiveTopic] = useState(null);
  const [topicLoading, setTopicLoading] = useState(false);
  const containerRef = useRef(null);

  // Initial load — list of topics
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await fetch(`${BASE_API_URL}/prealgebra/topics`);
        if (!r.ok) throw new Error(`topics ${r.status}`);
        const data = await r.json();
        if (!cancelled) setTopics(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // If URL has ?slug= or ?id=, auto-open that topic on first load
  const initialOpenAttempted = useRef(false);
  useEffect(() => {
    if (initialOpenAttempted.current) return;
    if (loading || topics.length === 0) return;
    const slug = searchParams.get("slug");
    const id = searchParams.get("id");
    if (slug || id) {
      const t = topics.find(x => (slug && x.slug === slug) || (id && String(x.id) === String(id)));
      if (t) openTopic(t);
    }
    initialOpenAttempted.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, topics]);

  const openTopic = useCallback(async (t) => {
    setTopicLoading(true);
    setActiveTopic({ ...t, questions: [] });
    setSearchParams({ slug: t.slug }, { replace: true });
    try {
      const r = await fetch(`${BASE_API_URL}/prealgebra/topic?id=${t.id}`);
      if (!r.ok) throw new Error(`topic ${r.status}`);
      const data = await r.json();
      setActiveTopic(data);
      // Scroll to top of detail view
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e.message);
    } finally {
      setTopicLoading(false);
    }
  }, [setSearchParams]);

  const closeTopic = useCallback(() => {
    setActiveTopic(null);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const toggleLang = useCallback(() => {
    setLang((l) => (l === "english" ? "tamil" : "english"));
  }, []);

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} style={{
      minHeight: "100vh", background: C.bg,
      fontFamily: EN_FONT, color: C.ink,
    }}>
      {loading && (
        <div style={{ textAlign: "center", padding: 60, color: C.inkSoft }}>
          {lang === "tamil" ? "தலைப்புகள் ஏற்றப்படுகின்றன…" : "Loading topics…"}
        </div>
      )}

      {error && !loading && (
        <div style={{
          maxWidth: 600, margin: "60px auto", padding: 20, borderRadius: 12,
          background: C.badSoft, border: `1px solid ${C.bad}`, color: C.bad,
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {!loading && !error && !activeTopic && (
        <TopicList
          topics={topics}
          lang={lang}
          onLangToggle={toggleLang}
          onOpen={openTopic}
        />
      )}

      {!loading && !error && activeTopic && (
        topicLoading && (!activeTopic.questions || activeTopic.questions.length === 0)
          ? (
            <div style={{ textAlign: "center", padding: 60, color: C.inkSoft }}>
              {lang === "tamil" ? "பாடம் ஏற்றப்படுகிறது…" : "Loading lesson…"}
            </div>
          ) : (
            <TopicDetail
              topic={activeTopic}
              lang={lang}
              onBack={closeTopic}
              onLangToggle={toggleLang}
            />
          )
      )}
    </div>
  );
}
